import { useCallback, useState } from "react";
import { useAtom } from "jotai";
import { useSnackbar } from "notistack";
import {
  ProductDb,
  ProductRef,
  mapProduct,
  refreshCollectionMap,
} from "../../features/products/product";
import {
  duplicateProducts,
  modifyProductCollections,
  modifyCollectionItems,
} from "../../features/products/product.service";
import { productDbAtom } from "../../features/products/product.state";
import { logError } from "../../services/logging";

export const useProductOps = () => {
  const [productDb, setProductDb] = useAtom(productDbAtom);
  const { enqueueSnackbar } = useSnackbar();

  const updateProductCollections = useCallback(
    async (productId: string, collectionIds: string[], db: ProductDb) => {
      db = db ?? productDb;
      const p = db.productMap.get(productId);
      if (!p) return;

      const newCollectionIds = collectionIds.filter(
        (i) => !p.collections?.some((c) => c.group.id === i)
      );
      const removedCollectionIds =
        p.collections
          ?.filter((c) => !collectionIds.some((i) => c.group.id === i))
          .map((c) => c.group.id) ?? [];

      if (newCollectionIds.length === 0 && removedCollectionIds.length === 0)
        return;

      try {
        await modifyProductCollections(productId, collectionIds);

        newCollectionIds.forEach((id) => {
          const col = db.collectionMap.get(id);
          col.group.items = [...col.group.items, productId];
        });

        removedCollectionIds.forEach((id) => {
          const col = db.collectionMap.get(id);
          col.group.items = col.group.items.filter((i) => i !== productId);
        });

        const productMap = new Map(db.productMap);
        const collectionMap = new Map(db.collectionMap);
        refreshCollectionMap(collectionMap, productMap);
        const db1 = {
          ...db,
          collectionMap,
          productMap,
          version: db.version + 1,
        };
        setProductDb(db1);

        enqueueSnackbar(`Modified ${p.product.name} collections`, {
          variant: "success",
        });
      } catch (e) {
        logError(e);
        enqueueSnackbar(`Error modifying ${p.product.name} collections`, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, productDb, setProductDb]
  );

  const updateCollectionItems = useCallback(
    async (collectionId: string, productIds: string[], remove: boolean) => {
      const col = productDb.collectionMap.get(collectionId);
      if (!col) return;

      try {
        await modifyCollectionItems(collectionId, productIds, remove);

        if (remove) {
          col.group.items = col.group.items.filter(
            (i) => productIds.indexOf(i) < 0
          );
        } else {
          const newProductIds = productIds.filter(
            (i) => col.group.items.indexOf(i) < 0
          );
          col.group.items = [...col.group.items, ...newProductIds];
        }

        const productMap = new Map(productDb.productMap);
        const collectionMap = new Map(productDb.collectionMap);
        refreshCollectionMap(collectionMap, productMap);
        const db = { ...productDb, collectionMap, productMap };
        setProductDb(db);

        enqueueSnackbar(`Modified ${col.group.name} collection products`, {
          variant: "success",
        });
      } catch (e) {
        logError(e);
        enqueueSnackbar(`Error modifying ${col.group.name} collection`, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, productDb, setProductDb]
  );

  return { updateCollectionItems, updateProductCollections };
};

export const useDuplicateProducts = () => {
  const [productDb, setProductDb] = useAtom(productDbAtom);
  const { enqueueSnackbar } = useSnackbar();
  const [processing, setProcessing] = useState(false);

  const duplicate = useCallback(
    async (ids: string[]): Promise<ProductRef[] | null> => {
      if (ids.length === 0) return null;

      setProcessing(true);
      try {
        const result = await duplicateProducts(ids);
        if (result.success && result.value?.length > 0) {
          const newProductMap = new Map(productDb.productMap);
          const newCollectionMap = new Map(productDb.collectionMap);
          const duplicatedRefs: ProductRef[] = [];

          result.value.forEach((p, index) => {
            const r = mapProduct(
              p,
              productDb.categoryMap,
              productDb.optionMap,
              productDb.storageUrl
            );
            newProductMap.set(r.product.id, r);
            duplicatedRefs.push(r);

            // Add duplicated product to the same collections as the original
            const originalId = ids[index];
            const originalRef = productDb.productMap.get(originalId);
            originalRef?.collections?.forEach((col) => {
              const colRef = newCollectionMap.get(col.group.id);
              if (colRef && !colRef.group.items.includes(p.id)) {
                colRef.group.items = [...colRef.group.items, p.id];
              }
            });
          });

          // Refresh collection map to update product references
          refreshCollectionMap(newCollectionMap, newProductMap);

          setProductDb({
            ...productDb,
            productMap: newProductMap,
            collectionMap: newCollectionMap,
            version: productDb.version + 1,
          });

          const message =
            result.value.length === 1
              ? `Product duplicated: ${result.value[0].name}`
              : `Duplicated ${result.value.length} product(s)`;
          enqueueSnackbar(message, { variant: "success" });

          return duplicatedRefs;
        } else {
          enqueueSnackbar(
            result.message ||
              result.errors?.[0] ||
              "Failed to duplicate product(s)",
            { variant: "error" }
          );
          return null;
        }
      } catch {
        enqueueSnackbar("Failed to duplicate product(s)", { variant: "error" });
        return null;
      } finally {
        setProcessing(false);
      }
    },
    [productDb, setProductDb, enqueueSnackbar]
  );

  return { duplicate, processing };
};
